import { useSocket } from '../../hooks/useSocket';
import { useTranslation } from 'react-i18next';

/**
 * Compact socket-connection indicator.
 *
 * Visual budget: a 8px coloured dot at every viewport, with a status label
 * that only renders at `md+` (where there's room without crowding the
 * workspace switcher or user menu). At smaller widths the dot is the whole
 * indicator — operators can still read connection state at a glance.
 *
 * States: connecting (warn, pulsing) · connected (ok) · error (danger) ·
 * disconnected (muted).
 */
export function ConnectionStatus() {
  const { isConnected, isConnecting, error } = useSocket();
  const { t } = useTranslation();

  // Error only wins when actually disconnected — generic socket 'error'
  // events (e.g. SocketContext.tsx onError handler) set `error` without
  // clearing `isConnected`, so without this guard a transient warning
  // would flash the indicator red while the socket is still healthy.
  const state =
    isConnecting ? 'connecting'
    : error && !isConnected ? 'error'
    : isConnected ? 'connected'
    : 'disconnected';

  const config = CONFIG[state];

  return (
    <span
      className="inline-flex items-center gap-1.5"
      role="status"
      aria-live="polite"
      aria-label={t(config.label)}
      title={t(config.label)}
    >
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${config.dot}`} />
      <span className={`hidden md:inline text-xs font-medium ${config.text}`}>{t(config.label)}</span>
    </span>
  );
}

// Module-scope so we don't reallocate on every render.
const CONFIG = {
  connecting:   { dot: 'bg-warn animate-pulse', text: 'text-warn',   label: 'connection.connecting' },
  connected:    { dot: 'bg-ok',             text: 'text-ok', label: 'connection.connected' },
  error:        { dot: 'bg-danger',                 text: 'text-danger',     label: 'connection.error' },
  disconnected: { dot: 'bg-muted',                text: 'text-muted',    label: 'connection.disconnected' },
} as const;
